import Dexie from 'dexie';
import type { BlueprintFieldsFragment } from '@/generated/graphql';

export interface LocalFile {
  id: string; // matches ProjectFile._id
  path: string; // local file path
  handle: FileSystemFileHandle;
  quickHash: string;
  fullHash: string;
  version: number;
  lastSynced: Date;
  lastModified: Date;
}

export interface LocalBlueprint {
  blueprintId?: string;
  version?: number;
  blueprint?: BlueprintFieldsFragment;
  id: string;
  revisionId: string;
  path: string;
  fullHash: string;
  downloadedAt: Date;
  title: string;
}

export class ProjectSyncDB extends Dexie {
  blueprints!: Dexie.Table<LocalBlueprint, string>;
  localFiles!: Dexie.Table<LocalFile, string>;

  constructor(name = 'ProjectSyncDB') {
    super(name);
    this.version(1).stores({
      localFiles: 'id, path, quickHash, fullHash, version',
    });
    this.version(2).stores({
      localFiles: 'id, path, quickHash, fullHash, version',
      fxRacks: 'id, revisionId, downloadedAt',
    });
    this.version(3)
      .stores({
        blueprints: 'id, revisionId, downloadedAt',
      })
      .upgrade(async (transaction) => {
        const legacy = await transaction.table('fxRacks').toArray();
        if (legacy.length)
          await transaction
            .table('blueprints')
            .bulkPut(legacy.map(migrateLocalBlueprint));
      });
    this.version(4).stores({ fxRacks: null });
    this.version(5)
      .stores({ blueprints: 'id, blueprintId, revisionId, downloadedAt' })
      .upgrade(async (transaction) => {
        const table = transaction.table('blueprints');
        const records = await table.toArray();
        await table.clear();
        await table.bulkPut(
          records.map((record) => ({
            ...record,
            blueprintId: record.blueprintId || record.id,
            id: `${record.blueprintId || record.id}:${record.revisionId}`,
            version: record.version || 1,
          }))
        );
      });
  }
}

// Keep existing hashes, paths, and consent state. Only the feature vocabulary changes.
export function migrateLocalBlueprint(
  record: Omit<LocalBlueprint, 'blueprint'> & {
    rack?: Omit<
      BlueprintFieldsFragment,
      | '__typename'
      | 'blueprintType'
      | 'pluginName'
      | 'pluginVersion'
      | 'fileName'
    > & { __typename?: string };
  }
): LocalBlueprint {
  const { rack, ...saved } = record;
  return {
    ...saved,
    blueprint: rack
      ? {
          ...rack,
          __typename: 'BlueprintType',
          blueprintType: 'fxChain',
          pluginName: '',
          pluginVersion: '',
          fileName: record.path.split(/[\\/]/).pop() || `${record.title}.adg`,
        }
      : undefined,
  };
}

export const db = new ProjectSyncDB();
